/**
 * MoversTodayCard — top 10 newly auto-discovered symbols in the last 24 h.
 *
 * Sits on the admin Symbol Registry page so ops can spot fresh Mansa or FMP
 * listings without scrolling the 100+ row registry table.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, RefreshCw, ArrowUpRight } from "lucide-react";

function fmtRelative(iso) {
    if (!iso) return "—";
    const then = new Date(iso).getTime();
    const now  = Date.now();
    const min  = Math.floor((now - then) / 60000);
    if (min < 1)   return "just now";
    if (min < 60)  return `${min}m ago`;
    const h = Math.floor(min / 60);
    if (h < 24)    return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

export default function MoversTodayCard() {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/symbols/movers?hours=24&limit=10");
            setData(data);
        } catch {
            /* silent */
        } finally { setLoading(false); }
    }
    useEffect(() => {
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, []);

    const movers = data?.movers || [];

    return (
        <Card data-testid="movers-today-card">
            <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    Movers today · last 24 h
                    {data?.total_new != null && (
                        <Badge variant="outline" className="ml-2 text-emerald-500 border-emerald-500/30">
                            +{data.total_new} new
                        </Badge>
                    )}
                </CardTitle>
                <Button
                    variant="ghost" size="sm"
                    onClick={load} disabled={loading}
                    data-testid="movers-today-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </CardHeader>
            <CardContent>
                {movers.length === 0 ? (
                    <div className="text-xs text-muted-foreground p-6 text-center border border-dashed border-border rounded">
                        No new listings in the last 24 hours. Next Mansa reconciliation runs on the hour.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {movers.map((m) => (
                            <div
                                key={m.symbol}
                                data-testid={`mover-row-${m.symbol}`}
                                className="flex items-center justify-between border border-border rounded-lg px-3 py-2 hover:bg-accent/40 transition-colors"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <ArrowUpRight className="w-4 h-4 text-emerald-500 shrink-0" />
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-mono font-bold">{m.symbol}</span>
                                            <span className="text-[10px] uppercase px-1 py-px rounded bg-muted text-muted-foreground">{m.country}</span>
                                            <span className="text-[10px] text-muted-foreground">{m.exchange}</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground truncate max-w-[240px]">{m.name}</div>
                                    </div>
                                </div>
                                <div className="text-right shrink-0 ml-2">
                                    <Badge
                                        variant={m.source === "mansa" ? "default" : m.source === "fmp" ? "secondary" : "outline"}
                                        className="text-[10px]"
                                    >
                                        {m.source}
                                    </Badge>
                                    <div className="text-[10px] text-muted-foreground mt-1">
                                        first seen {fmtRelative(m.first_seen)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
